import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import searchIcon from "@assets/icons/search-icon.svg";
import { EvaluationGroupTypes } from "@constants/appConstants";
import type { EvaluationGroupType } from "@constants/appConstants";
import styles from "./Dashboard.module.css";
import Header from "@components/header/Header";
import AssignTaskConfirmation from "@modals/assigntaskconfirmation/AssignTaskConfirmation";
import UnAssignConfirmation from "@modals/unassignconfirmation/UnAssignConfirmation";
import Pagination from "@components/common/pagination/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { setComponentToShow } from "@store/ModalToShow";
import {
  useAssignedEvaluationTask,
  useEvaluationGroups,
  useEvaluationMutations,
} from "@hooks/evaluation";

function Dashboard() {
  const [filterType, setFilterType] = useState<EvaluationGroupType>(
    EvaluationGroupTypes.ALL
  );
  const {
    task: assignedEvaluationTask,
    isLoading: isAssignedTaskLoading,
    error: assignedTaskError,
    hasTask,
    refetch: refetchAssignedTask,
  } = useAssignedEvaluationTask();
  const {
    groups: evaluationGroups,
    isLoading: isEvaluationGroupsLoading,
    error: evaluationGroupsError,
    hasGroups,
    refetch: refetchEvaluationGroups,
  } = useEvaluationGroups(filterType === EvaluationGroupTypes.CHALLENGED);
  const { unassignTask, isUnassigning } = useEvaluationMutations();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const dispatch = useDispatch();
  const componentName = useSelector(
    (state: any) => state.modalToShow.componentName
  );
  const navigate = useNavigate();

  const filteredData = useMemo(() => {
    let filtered = evaluationGroups ? [...evaluationGroups] : [];
    if (searchTerm) {
      filtered = filtered.filter((group) =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [searchTerm, evaluationGroups]);

  const totalPages = Math.ceil(filteredData?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleSelectTask = (groupId: string) => {
    if (hasTask) return; // Prevent selection when task is assigned
    setSelectedGroupId(groupId);
    dispatch(setComponentToShow("AssignTaskConfirmation"));
  };

  const handleClearTaskSelection = () => {
    setSelectedGroupId(null);
    dispatch(setComponentToShow(""));
  };

  const handleEvaluate = () => {
    if (selectedGroupId) {
      // Navigate with evaluationGroupId in router state
      navigate(`/evaluation?evaluation_group_id=${selectedGroupId}`, {
        state: { mode: "assign", evaluationGroupId: selectedGroupId },
      });
    }
    handleClearTaskSelection();
  };

  const handleResumeEvaluation = () => {
    if (!assignedEvaluationTask?.id) return;
    // Navigate with assignmentId in router state for resume flow
    navigate(`/evaluation?evaluation_group_id=${assignedEvaluationTask.evaluation_group_id}`, {
      state: { mode: "resume", assignmentId: assignedEvaluationTask.id },
    });
  };

  const handleUnAssign = async () => {
    if (!assignedEvaluationTask?.id) return;
    try {
      await unassignTask(assignedEvaluationTask.id);
      // Refetch both queries after successful unassign
      await refetchAssignedTask();
      await refetchEvaluationGroups();
      dispatch(setComponentToShow(""));
    } catch (error) {
      // Error handling is done in the mutation hook via toast
      console.error("Failed to unassign task:", error);
    }
  };

  const handleFilterChange = (type: EvaluationGroupType) => {
    setFilterType(type);
    setCurrentPage(1);
  };
  const renderModal = () => {
    switch (componentName) {
      case "AssignTaskConfirmation":
        return (
          <AssignTaskConfirmation
            isOpen={true}
            onClose={() => handleClearTaskSelection()}
            onStartEvaluation={handleEvaluate}
          />
        );
      case "UnAssignConfirmation":
        return (
          <UnAssignConfirmation
            isOpen={true}
            onClose={() => handleClearTaskSelection()}
            onUnAssign={handleUnAssign}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <div className={styles.dashboardContainer}>
        <div className={styles.mainContent}>
          <div className={styles.header}>
            <div className={styles.title}>
              <span>Evaluation Groups</span>
            </div>
          </div>

          <div className={styles.searchContainer}>
            <div className={styles.inputContainer}>
              <input
                type="text"
                placeholder="Search by Evaluation Group Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <img src={searchIcon} alt="Search Icon" />
            </div>
            <div className={styles.searchButtonContainer}>
              <motion.button
                className={styles.searchButton}
                onClick={handleSearch}
                whileTap={{ translateY: 2 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                Search
              </motion.button>
            </div>
          </div>
          <div className={styles.assignedEvaluationTaskContainer}>
            {isAssignedTaskLoading && (
              <div className={styles.evaluationGroupsListTable}>
                <div className={styles.loadingMessage}>
                  Loading assigned task...
                </div>
              </div>
            )}
            {assignedTaskError && (
              <div className={styles.evaluationGroupsListTable}>
                <div className={styles.errorMessage}>
                  Error loading assigned task. Please try again.
                </div>
              </div>
            )}
            {hasTask && assignedEvaluationTask && (
              <div className={styles.evaluationGroupsListTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Assigned Evaluation Task</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={assignedEvaluationTask.id}>
                      <td>{assignedEvaluationTask.id}</td>
                      <td>
                        <button
                          className={styles.unassignButton}
                          onClick={() =>
                            dispatch(setComponentToShow("UnAssignConfirmation"))
                          }
                          disabled={isUnassigning}
                        >
                          {isUnassigning ? "Unassigning..." : "unassign"}
                        </button>
                      </td>
                      <td>
                        <button
                          className={styles.evaluateButton}
                          onClick={handleResumeEvaluation}
                        >
                          resume evaluation
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className={styles.evaluationGroupsListContainer}>
            {hasTask && (
              <div className={styles.disabledMessage}>
                Finish or unassign your current task to pick a new one.
              </div>
            )}
            <div className={styles.evaluationGroupsListActionsContainer}>
              {/* Filter Buttons */}
              <div className={styles.filterButtons}>
                <motion.button
                  className={`${styles.filterButton} ${
                    filterType === EvaluationGroupTypes.ALL
                      ? styles.activeFilter
                      : ""
                  }`}
                  onClick={() => handleFilterChange(EvaluationGroupTypes.ALL)}
                  disabled={hasTask}
                  animate={{
                    opacity: filterType === EvaluationGroupTypes.ALL ? 1 : 0.7,
                  }}
                  whileTap={{ scale: 0.97 }}
                  transition={{
                    opacity: { duration: 0.2, ease: "easeOut" },
                  }}
                >
                  All Evaluation Groups
                </motion.button>
                <motion.button
                  className={`${styles.filterButton} ${
                    filterType === EvaluationGroupTypes.CHALLENGED
                      ? styles.activeFilter
                      : ""
                  }`}
                  onClick={() =>
                    handleFilterChange(EvaluationGroupTypes.CHALLENGED)
                  }
                  disabled={hasTask}
                  animate={{
                    opacity:
                      filterType === EvaluationGroupTypes.CHALLENGED ? 1 : 0.7,
                  }}
                  whileTap={{ scale: 0.97 }}
                  transition={{
                    opacity: { duration: 0.2, ease: "easeOut" },
                    scale: { duration: 0.15, ease: "easeOut" },
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    Challenges
                    <AnimatePresence>
                      {filterType === EvaluationGroupTypes.CHALLENGED && (
                        <motion.span
                          className={styles.challengedBadge}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                          }}
                          // style={{ display: "inline-block" }}
                        >
                          ({filteredData.length})
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </span>
                </motion.button>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                disabled={hasTask}
              />
            </div>

            <div className={styles.evaluationGroupsListTable}>
              {isEvaluationGroupsLoading && (
                <div className={styles.loadingMessage}>
                  Loading evaluation groups...
                </div>
              )}
              {evaluationGroupsError && (
                <div className={styles.errorMessage}>
                  Error loading evaluation groups.{" "}
                  <button
                    onClick={() => refetchEvaluationGroups()}
                    className={styles.retryButton}
                  >
                    Retry
                  </button>
                </div>
              )}
              {!isEvaluationGroupsLoading &&
                !evaluationGroupsError &&
                (currentData.length > 0 ? (
                  <table
                    className={`${hasTask ? styles.disabledTable : ""}`}
                    aria-disabled={hasTask}
                  >
                    <thead>
                      <tr>
                        <th>Evaluation Group</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentData.map((group) => (
                        <tr key={group.id}>
                          <td>{group.name}</td>
                          <td>
                            <button
                              className={styles.evaluateButton}
                              onClick={() => handleSelectTask(group.id)}
                              disabled={hasTask}
                              aria-disabled={hasTask}
                            >
                              evaluate
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={styles.emptyMessage}>
                    {hasGroups
                      ? "No evaluation groups match your search criteria."
                      : "No evaluation groups found."}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      {renderModal()}
    </>
  );
}

export default Dashboard;
